import { Link } from 'react-router-dom';
import { useCommerceEnabled, useStorefrontContent } from '../lib/hooks';
import { isCommercePath } from '@mlm/shared';
import { storefrontAssetUrl } from '../../lib/api';

/** Internal hrefs (starting with "/") navigate via the router; anything else is
 * an external anchor. Keeps storefront nav SPA-fast without a full reload. */
function FooterLink({
  href,
  children,
  dark,
}: {
  href: string;
  children: React.ReactNode;
  dark?: boolean;
}) {
  const cls = dark
    ? 'mb-[9px] block cursor-pointer text-[13px] text-white/80 hover:text-lms-gold'
    : 'mb-[9px] block cursor-pointer text-[13px] text-[#666] hover:text-o';
  return href.startsWith('/') ? (
    <Link to={href} className={cls}>
      {children}
    </Link>
  ) : (
    <a href={href} className={cls}>
      {children}
    </a>
  );
}

/**
 * `variant='dark'` is the LEARNING palette: the deepest brand green with a gold
 * mark and gold link hovers, driven by the `lms*` theme tokens. Structure and
 * data are identical to the light footer — only the colours and the brand block
 * change, so a tenant's footer columns are edited in exactly one place.
 */
export function Footer({ variant = 'light' }: { variant?: 'light' | 'dark' } = {}) {
  const dark = variant === 'dark';
  const { footer, social } = useStorefrontContent().data;
  const socialLinks = social.filter((s) => s.url.trim());
  // On an information-only site the shop, sign-up and member portal routes
  // aren't mounted, so CMS links pointing at them would dead-end on the
  // catch-all redirect. Drop them here rather than rewriting stored content —
  // live tenants hold their own admin-edited copies of these columns.
  const commerce = useCommerceEnabled();
  const columns = commerce
    ? footer.columns
    : footer.columns
        .map((c) => ({ ...c, links: c.links.filter((l) => !isCommercePath(l.href)) }))
        .filter((c) => c.links.length > 0);
  const heading = dark
    ? 'mb-3.5 text-xs uppercase tracking-[0.07em] text-white/55'
    : 'mb-3.5 text-xs uppercase tracking-[0.07em] text-[#888]';
  return (
    <footer
      className={dark ? 'mt-[72px] bg-lms-deep text-white/85' : 'mt-9 border-t-4 border-o bg-white'}
    >
      <div className="mx-auto max-w-[1180px] px-4">
        <div
          className={`grid gap-6 md:grid-cols-[1.6fr_repeat(4,1fr)] ${dark ? 'py-10' : 'py-[34px]'}`}
        >
          <div>
            <div className="flex items-center gap-2">
              {dark && (
                <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-lms-gold font-lms-heading text-[15px] font-extrabold text-lms-deep">
                  {footer.brandName.slice(0, 1).toUpperCase()}
                </span>
              )}
              <div
                className={
                  dark
                    ? 'font-lms-heading text-[22px] font-extrabold tracking-[-0.02em] text-white'
                    : 'text-2xl font-extrabold tracking-tight text-o'
                }
              >
                {footer.brandName}
              </div>
            </div>
            <p
              className={`mt-3 max-w-[34ch] text-[13px] leading-[1.6] ${dark ? 'text-white/60' : 'text-[#777]'}`}
            >
              {footer.brandBlurb}
            </p>
          </div>
          {columns.map((c) => (
            <div key={c.heading}>
              <h4 className={heading}>{c.heading}</h4>
              {c.links.map((l) => (
                <FooterLink key={l.label} href={l.href} dark={dark}>
                  {l.label}
                </FooterLink>
              ))}
            </div>
          ))}
          {socialLinks.length > 0 && (
            <div>
              <h4 className={heading}>Follow Us</h4>
              <div className="flex flex-col gap-2.5">
                {socialLinks.map((s) => (
                  <a
                    key={s.platform + s.url}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 text-[13px] ${dark ? 'text-white/80 hover:text-gold' : 'text-[#666] hover:text-o'}`}
                  >
                    {s.logoKey ? (
                      <img
                        src={storefrontAssetUrl(s.logoKey)}
                        alt=""
                        className="h-5 w-5 flex-none rounded object-contain"
                      />
                    ) : (
                      <span
                        className={`h-5 w-5 flex-none rounded ${dark ? 'bg-white/15' : 'bg-[#f0f0f0]'}`}
                      />
                    )}
                    {s.platform}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <div
          className={`flex flex-wrap justify-between gap-2 border-t py-[18px] text-xs ${dark ? 'border-white/15 text-white/50' : 'border-[#ececec] text-[#999]'}`}
        >
          <span>{footer.copyrightText}</span>
          <span>{footer.permitText}</span>
        </div>
      </div>
    </footer>
  );
}
